import { useState, useRef, useEffect, type PointerEvent, type WheelEvent } from "react";
import {
  Ban,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Crop as CropIcon,
  Check,
  X,
} from "lucide-react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { Slider } from "./Slider";

export type CropAspectRatio = "1:1" | "4:3" | "16:9" | "free";

export interface ImageCropperModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Image source to crop (dataUrl, objectUrl, or https URL) */
  imageSrc?: string | null;
  /** Aspect ratio presets */
  aspectRatio?: CropAspectRatio;
  /** If true, locks aspect ratio to preset and hides the aspect ratio selector toolbar (e.g. for avatar) */
  lockAspectRatio?: boolean;
  /** Modal header title */
  title?: string;
  /** Callback returning cropped Blob and DataURL */
  onCrop: (result: { blob: Blob; dataUrl: string }) => void;
}

const aspectRatioValues: Record<CropAspectRatio, number | null> = {
  "1:1": 1,
  "4:3": 4 / 3,
  "16:9": 16 / 9,
  free: null,
};

/**
 * ImageCropperModal - Lightweight, zero-dependency HTML5 Canvas image cropper for React 19.
 * Supports pan, zoom, rotate, aspect ratio switching, and crisp canvas export.
 */
export function ImageCropperModal({
  open,
  onOpenChange,
  imageSrc,
  aspectRatio = "1:1",
  lockAspectRatio = false,
  title = "Cắt & Điều Chỉnh Hình Ảnh",
  onCrop,
}: ImageCropperModalProps) {
  const [selectedRatio, setSelectedRatio] = useState<CropAspectRatio>(aspectRatio);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSelectedRatio(aspectRatio);
  }, [aspectRatio, open]);

  // Reset transforms on open or new image
  useEffect(() => {
    if (open) {
      setZoom(1);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
      setSelectedRatio(aspectRatio);
    }
  }, [open, imageSrc, aspectRatio]);

  // Pointer drag events for panning
  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setOffset({
      x: dragStartRef.current.offsetX + dx,
      y: dragStartRef.current.offsetY + dy,
    });
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  // Wheel zoom
  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setZoom((prev) => Math.min(Math.max(Number((prev + delta).toFixed(2)), 0.8), 3));
  };

  const handleRotate = () => {
    setRotation((r) => (r + 90) % 360);
  };

  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  };

  // Perform canvas crop
  const handleConfirmCrop = async () => {
    if (!imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;
    const cropBox = container.getBoundingClientRect();

    // Determine target dimensions
    const ratio = aspectRatioValues[selectedRatio];
    let cropWidth = cropBox.width * 0.8;
    let cropHeight = ratio ? cropWidth / ratio : cropBox.height * 0.8;

    if (cropHeight > cropBox.height * 0.85) {
      cropHeight = cropBox.height * 0.85;
      cropWidth = ratio ? cropHeight * ratio : cropBox.width * 0.8;
    }

    const canvas = document.createElement("canvas");
    // Crisp export resolution
    const exportWidth = Math.max(cropWidth * 2, 600);
    const exportHeight = ratio ? exportWidth / ratio : Math.max(cropHeight * 2, 600);

    canvas.width = exportWidth;
    canvas.height = exportHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Fill white background for transparent images/jpeg export
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, exportWidth, exportHeight);

    ctx.save();
    // Center of canvas
    ctx.translate(exportWidth / 2, exportHeight / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Apply offset relative to export scale
    const scaleFactor = exportWidth / cropWidth;
    ctx.translate(offset.x * scaleFactor, offset.y * scaleFactor);

    // Render original image centered
    const imgAspect = img.naturalWidth / img.naturalHeight;
    let renderW = exportWidth;
    let renderH = renderW / imgAspect;

    if (renderH < exportHeight) {
      renderH = exportHeight;
      renderW = renderH * imgAspect;
    }

    ctx.drawImage(img, -renderW / 2, -renderH / 2, renderW, renderH);
    ctx.restore();

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        onCrop({ blob, dataUrl });
        onOpenChange(false);
      },
      "image/jpeg",
      0.92
    );
  };

  if (!imageSrc) return null;

  const currentRatio = aspectRatioValues[selectedRatio];

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="lg"
      preventOutsideClose={true}
      bodyClassName="p-0 overflow-hidden flex flex-col"
    >
      <div className="flex flex-col select-none max-h-[85vh]">
        {/* Aspect Ratio Selector Sub-bar (hidden when ratio is locked, e.g. for avatar) */}
        {!lockAspectRatio && (
          <div className="flex items-center justify-between border-b border-border/80 px-4 py-2 bg-surface shrink-0">
            <span className="text-caption font-semibold text-text">
              Tỉ lệ khung hình:
            </span>

            <div className="flex items-center gap-1 rounded-control bg-bg p-0.5 border border-border text-caption">
              {(["1:1", "4:3", "16:9", "free"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setSelectedRatio(r)}
                  className={`px-2.5 py-0.5 rounded-sm font-semibold transition-all cursor-pointer ${
                    selectedRatio === r
                      ? "bg-primary text-on-primary shadow-xs"
                      : "text-text-secondary hover:text-text"
                  }`}
                >
                  {r === "1:1" ? "1:1 (Avatar/Logo)" : r}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Canvas / Viewport Area */}
        <div
          ref={containerRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onWheel={handleWheel}
          className="relative h-[380px] w-full overflow-hidden bg-black/90 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"
        >
          {/* Draggable & Scalable Image */}
          <img
            ref={imageRef}
            src={imageSrc}
            alt="Source for cropping"
            draggable={false}
            className="max-h-[300px] max-w-[80%] object-contain transition-transform duration-75 pointer-events-none select-none"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            }}
          />

          {/* Semi-transparent Crop Guide Overlay */}
          <div
            className={`pointer-events-none absolute border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] ${
              selectedRatio === "1:1" ? "rounded-full" : "rounded-control"
            }`}
            style={{
              width: currentRatio ? (currentRatio >= 1 ? "260px" : `${260 * currentRatio}px`) : "260px",
              height: currentRatio ? (currentRatio >= 1 ? `${260 / currentRatio}px` : "260px") : "200px",
            }}
          >
            {/* Rule of thirds lines */}
            <div className="size-full grid grid-cols-3 grid-rows-3 opacity-30 border border-white/40">
              <div className="border-r border-b border-white" />
              <div className="border-r border-b border-white" />
              <div className="border-b border-white" />
              <div className="border-r border-b border-white" />
              <div className="border-r border-b border-white" />
              <div className="border-b border-white" />
              <div className="border-r border-white" />
              <div className="border-r border-white" />
              <div />
            </div>
          </div>

          <div className="absolute bottom-2 left-3 rounded-md bg-black/60 px-2 py-1 text-[11px] text-white/80 backdrop-blur-xs">
            Kéo để di chuyển · Cuộn chuột hoặc dùng thanh trượt bên dưới để phóng to
          </div>
        </div>

        {/* Toolbar Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/80 px-5 py-3 bg-surface shrink-0">
          <div className="flex items-center gap-3 flex-1 min-w-[220px] max-w-sm">
            <ZoomOut className="size-4 text-text-muted shrink-0" />
            <Slider
              value={[zoom]}
              min={0.8}
              max={3}
              step={0.05}
              onValueChange={([val]) => setZoom(val ?? 1)}
              className="flex-1"
            />
            <ZoomIn className="size-4 text-text-muted shrink-0" />
            <span className="font-mono text-caption text-text-muted w-10 text-right">
              {Math.round(zoom * 100)}%
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <IconButton
              icon={<RotateCw className="size-4" />}
              size="sm"
              variant="secondary"
              onClick={handleRotate}
              label="Xoay 90 độ"
            />
            <Button size="sm" variant="ghost" onClick={handleReset}>
              Đặt lại
            </Button>
            <div className="mx-1 h-4 w-px bg-border" />
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<Ban className="size-4" />}
              onClick={() => onOpenChange(false)}
            >
              Hủy
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleConfirmCrop}
              className="flex items-center gap-1.5"
            >
              <Check className="size-4" />
              <span>Cắt ảnh</span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
